import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  getUserNotification,
  markNotificationAsRead,
  markNotificationAsSeen,
  markNotificationAsSeenById,
  sendNotificationInvite
} from '../services/notification';
import { handleApiError } from '../services/errorHandler';
import { PAGINATION_LIMITS, STORAGE_KEYS } from '../config/notificationConfig';
import { NOTIFICATION_CATEGORIES, NOTIFICATION_ACTION_TYPES } from '../utility/types/NotificationConfig';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { trackChallengeJoinedViaInvite } from '../services/activityService';

interface UseNotificationListReturn {
  notifications: {
    organized: any[];
    challenges: any[];
    watching: any[];
  };
  loading: {
    initial: boolean;
    loadMore: boolean;
    markAsRead: boolean;
  };
  pagination: {
    lastEvaluatedKey: string | null;
    allLoaded: boolean;
  };
  unreadCounts: {
    organizer: number;
    challenger: number;
    watching: number;
  };
  actions: {
    fetchNotifications: (accessToken: string, loadMore?: boolean) => Promise<void>;
    markAsRead: (accessToken: string, notificationId: string, onSuccess?: () => void) => Promise<void>;
    markAsSeen: (accessToken: string, category: string) => Promise<void>;
    markAsSeenById: (accessToken: string, notificationId: string) => Promise<void>;
    sendInvite: (accessToken: string, encodedSession: string) => Promise<string | null>;
    updateUnreadCounts: () => void;
    addRealTimeNotification: (notification: any) => void;
    updateNotificationStatus: (notificationId: string, updates: any) => void;
  };
}

export const useNotificationList = (): UseNotificationListReturn => {
  // State management
  const [notifications, setNotifications] = useState({
    organized: [],
    challenges: [],
    watching: [],
  });

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
    markAsRead: false,
  });

  const [pagination, setPagination] = useState({
    lastEvaluatedKey: null,
    allLoaded: false,
  });

  const [unreadCounts, setUnreadCounts] = useState({
    organizer: 0,
    challenger: 0,
    watching: 0,
  });

  // Track loaded notifications to prevent duplicates
  const loadedNotificationIds = useRef(new Set<string>());

  const fetchNotifications = useCallback(
    async (accessToken: string, loadMore = false) => {
      try {
        if (loadMore && (pagination.allLoaded || !pagination.lastEvaluatedKey)) {
          return;
        }

        // Set appropriate loading state
        if (loadMore) {
          setLoading(prev => ({ ...prev, loadMore: true }));
        } else {
          setLoading(prev => ({ ...prev, initial: true }));
          loadedNotificationIds.current.clear();
          setPagination({ lastEvaluatedKey: null, allLoaded: false });
        }

        const params = {
          accessToken,
          limit: loadMore ? PAGINATION_LIMITS.LOAD_MORE : PAGINATION_LIMITS.INITIAL_LOAD,
          lastEvaluatedKey: loadMore ? pagination.lastEvaluatedKey : null,
        };

        const response = await getUserNotification(params);

        if (response.statusCode !== 200 && response.statusCode !== 201) {
          handleApiError(response.statusCode, response.body?.error || '');
          return;
        }

        // Filter out already loaded notifications
        const newNotifications = response.body.notifications.filter(
          (notification: any) => !loadedNotificationIds.current.has(notification.notificationId)
        );

        // Update loaded IDs tracking
        newNotifications.forEach((notification: any) =>
          loadedNotificationIds.current.add(notification.notificationId)
        );

        // Update pagination
        setPagination({
          lastEvaluatedKey: response.body.lastEvaluatedKey || null,
          allLoaded: !response.body.lastEvaluatedKey,
        });

        // Categorize notifications
        const categorized = {
          organized: newNotifications.filter(
            (n: any) => n.category === NOTIFICATION_CATEGORIES.ORGANISER
          ),
          challenges: newNotifications.filter(
            (n: any) => n.category === NOTIFICATION_CATEGORIES.CHALLENGER
          ),
          watching: newNotifications.filter(
            (n: any) => n.category === NOTIFICATION_CATEGORIES.AUDIENCE
          ),
        };

        // Update state
        if (loadMore) {
          setNotifications(prev => ({
            organized: [...prev.organized, ...categorized.organized],
            challenges: [...prev.challenges, ...categorized.challenges],
            watching: [...prev.watching, ...categorized.watching],
          }));
        } else {
          setNotifications(categorized);
        }

        const currentNotifications = loadMore
          ? {
            organized: [...notifications.organized, ...categorized.organized],
            challenges: [...notifications.challenges, ...categorized.challenges],
            watching: [...notifications.watching, ...categorized.watching],
          }
          : categorized;

        // Count all notifications with isSeen: 0 for each category
        const organizerCount = currentNotifications.organized.filter(
          (n: any) => n.isSeen === 0
        ).length;

        const challengerCount = currentNotifications.challenges.filter(
          (n: any) => n.isSeen === 0
        ).length;

        const watchingCount = currentNotifications.watching.filter(
          (n: any) => n.isSeen === 0
        ).length;


        setUnreadCounts({
          organizer: organizerCount,
          challenger: challengerCount,
          watching: watchingCount
        });

        await AsyncStorage.setItem(STORAGE_KEYS.UNREAD_CHALLENGE, challengerCount.toString());

      } catch (error) {
        toast.error(error.message || 'Failed to fetch notifications');
      } finally {
        setLoading({ initial: false, loadMore: false, markAsRead: false });
      }
    },
    [pagination, notifications.challenges]
  );

  const markAsRead = useCallback(
    async (accessToken: string, notificationId: string, onSuccess?: () => void) => {
      // Find the notification before removing it (for isSeen check)
      const notification =
        notifications.organized.find(n => n.notificationId === notificationId) ||
        notifications.challenges.find(n => n.notificationId === notificationId) ||
        notifications.watching.find(n => n.notificationId === notificationId);

      // Optimistically remove notification from UI immediately
      setNotifications(prev => ({
        organized: prev.organized.filter(n => n.notificationId !== notificationId),
        challenges: prev.challenges.filter(n => n.notificationId !== notificationId),
        watching: prev.watching.filter(n => n.notificationId !== notificationId),
      }));

      // Update counts optimistically (only if notification was unseen)
      setUnreadCounts(prev => {
        if (notification && notification.isSeen === 0) {
          const category = notification.category;
          if (category === NOTIFICATION_CATEGORIES.ORGANISER) {
            return { ...prev, organizer: Math.max(0, prev.organizer - 1) };
          } else if (category === NOTIFICATION_CATEGORIES.CHALLENGER) {
            return { ...prev, challenger: Math.max(0, prev.challenger - 1) };
          } else if (category === NOTIFICATION_CATEGORIES.AUDIENCE) {
            return { ...prev, watching: Math.max(0, prev.watching - 1) };
          }
        }
        return prev;
      });

      // Store original state for rollback
      const originalNotifications = notifications;
      const originalCounts = unreadCounts;

      try {
        // Call both APIs in parallel: mark as read AND mark as seen
        const [readResponse, seenResponse] = await Promise.all([
          markNotificationAsRead(accessToken, notificationId),
          notification && notification.isSeen === 0
            ? markNotificationAsSeenById(accessToken, notificationId)
            : Promise.resolve({ statusCode: 200 })
        ]);

        if (readResponse.statusCode === 200 && seenResponse.statusCode === 200) {
          // Success - optimistic update already applied
          loadedNotificationIds.current.delete(notificationId);

          // Call the onSuccess callback to refresh global notification counts
          if (onSuccess) {
            // Wait a bit to ensure backend is fully updated
            setTimeout(() => {
              onSuccess();
            }, 500);
          }
        } else {
          // Rollback on error
          setNotifications(originalNotifications);
          setUnreadCounts(originalCounts);
          toast.error('Failed to mark notification as read');
        }
      } catch (error) {
        // Rollback on error
        setNotifications(originalNotifications);
        setUnreadCounts(originalCounts);
        toast.error('Failed to mark notification as read');
      }
    },
    [notifications, unreadCounts]
  );

  const markAsSeen = useCallback(
    async (accessToken: string, category: string) => {
      try {
        const response = await markNotificationAsSeen(accessToken, category);

        if (response.statusCode === 200) {
          setNotifications(prev => {
            const updated = { ...prev };
            switch (category) {
              case NOTIFICATION_CATEGORIES.ORGANISER:
                updated.organized = prev.organized.map(n => ({ ...n, isSeen: 1 }));
                break;
              case NOTIFICATION_CATEGORIES.CHALLENGER:
                updated.challenges = prev.challenges.map(n => ({ ...n, isSeen: 1 }));
                break;
              case NOTIFICATION_CATEGORIES.AUDIENCE:
                updated.watching = prev.watching.map(n => ({ ...n, isSeen: 1 }));
                break;
            }
            return updated;
          });

          setUnreadCounts(prev => ({
            ...prev,
            [category === NOTIFICATION_CATEGORIES.ORGANISER ? 'organizer' :
              category === NOTIFICATION_CATEGORIES.CHALLENGER ? 'challenger' : 'watching']: 0
          }));
        }
      } catch (error) {
        console.error('Error marking notifications as seen:', error);
      }
    },
    []
  );

  const markAsSeenById = useCallback(
    async (accessToken: string, notificationId: string) => {
      // Store original state for rollback
      const originalNotifications = notifications;
      const originalCounts = unreadCounts;

      // Optimistically update the specific notification's isSeen status
      setNotifications(prev => {
        const updated = { ...prev };

        // Update in organized notifications
        updated.organized = prev.organized.map(n =>
          n.notificationId === notificationId ? { ...n, isSeen: 1 } : n
        );

        // Update in challenge notifications
        updated.challenges = prev.challenges.map(n =>
          n.notificationId === notificationId ? { ...n, isSeen: 1 } : n
        );

        // Update in watching notifications
        updated.watching = prev.watching.map(n =>
          n.notificationId === notificationId ? { ...n, isSeen: 1 } : n
        );

        return updated;
      });

      // Optimistically recalculate unread counts
      setNotifications(currentNotifications => {
        const organizerCount = currentNotifications.organized.filter(
          (n: any) => n.isSeen === 0
        ).length;

        const challengerCount = currentNotifications.challenges.filter(
          (n: any) => n.isSeen === 0
        ).length;

        const watchingCount = currentNotifications.watching.filter(
          (n: any) => n.isSeen === 0
        ).length;

        setUnreadCounts({
          organizer: organizerCount,
          challenger: challengerCount,
          watching: watchingCount
        });

        return currentNotifications;
      });

      try {
        const response = await markNotificationAsSeenById(accessToken, notificationId);

        if (response.statusCode !== 200) {
          // Rollback on error
          setNotifications(originalNotifications);
          setUnreadCounts(originalCounts);
          toast.error('Failed to mark notification as seen');
        }
      } catch (error) {
        // Rollback on error
        setNotifications(originalNotifications);
        setUnreadCounts(originalCounts);
        console.error('Error marking notification as seen by ID:', error);
        toast.error('Failed to mark notification as seen');
      }
    },
    [notifications, unreadCounts]
  );

  const sendInvite = useCallback(
    async (accessToken: string, encodedSession: string): Promise<string | null> => {
      try {
        const decodedSession = JSON.parse(atob(encodedSession));
        const eventId = decodedSession.eventId;

        if (eventId) {
          const response = await sendNotificationInvite(accessToken, eventId);

          if (response.statusCode === 200) {
            // Track the activity
            trackChallengeJoinedViaInvite(accessToken, eventId).catch(err => {
              console.error('Error tracking challenge join activity:', err);
            });
            return response.body.challengerNotificationId;
          } else {
            toast.error(response.body.error || 'An error occurred');
            return null;
          }
        }
        return null;
      } catch (error) {
        toast.error('Invalid session data.');
        return null;
      }
    },
    []
  );

  const updateUnreadCounts = useCallback((category?: string) => {
    // Reset specific category or all categories
    if (category) {
      setUnreadCounts(prev => ({ ...prev, [category]: 0 }));
    } else {
      // Reset all counts
      setUnreadCounts({ organizer: 0, challenger: 0, watching: 0 });
    }
  }, []);

  const updateNotificationStatus = useCallback(
    (notificationId: string, updates: any) => {
      setNotifications(prev => {
        const updated = { ...prev };

        // Update in organized notifications
        updated.organized = prev.organized.map(n =>
          n.notificationId === notificationId ? { ...n, ...updates } : n
        );

        // Update in challenge notifications
        updated.challenges = prev.challenges.map(n =>
          n.notificationId === notificationId ? { ...n, ...updates } : n
        );

        // Update in watching notifications
        updated.watching = prev.watching.map(n =>
          n.notificationId === notificationId ? { ...n, ...updates } : n
        );

        return updated;
      });
    },
    []
  );

  const addRealTimeNotification = useCallback(
    (notification: any) => {
      if (notification.category) {
        setNotifications(prev => {
          const newNotification = {
            ...notification,
            isRead: 0,
            isSeen: 0,
            createdAt: new Date().toISOString(),
          };

          const notificationId = notification.notificationId || notification.id;
          let updatedNotifications = prev;

          switch (notification.category) {
            case NOTIFICATION_CATEGORIES.ORGANISER:
              const isOrganizerDuplicate = prev.organized.some(n => n.notificationId === notificationId);
              if (isOrganizerDuplicate) {
                return prev;
              }
              updatedNotifications = {
                ...prev,
                organized: [newNotification, ...prev.organized],
              };
              break;

            case NOTIFICATION_CATEGORIES.CHALLENGER:
              const isChallengerDuplicate = prev.challenges.some(n => n.notificationId === notificationId);
              if (isChallengerDuplicate) {
                return prev;
              }
              updatedNotifications = {
                ...prev,
                challenges: [newNotification, ...prev.challenges],
              };
              break;

            case NOTIFICATION_CATEGORIES.AUDIENCE:
              const isAudienceDuplicate = prev.watching.some(n => n.notificationId === notificationId);
              if (isAudienceDuplicate) {
                return prev;
              }
              updatedNotifications = {
                ...prev,
                watching: [newNotification, ...prev.watching],
              };
              break;

            default:
              return prev;
          }

          // Count all notifications with isSeen: 0 for each category
          const organizerCount = updatedNotifications.organized.filter(
            (n: any) => n.isSeen === 0
          ).length;

          const challengerCount = updatedNotifications.challenges.filter(
            (n: any) => n.isSeen === 0
          ).length;

          const watchingCount = updatedNotifications.watching.filter(
            (n: any) => n.isSeen === 0
          ).length;

          setUnreadCounts({
            organizer: organizerCount,
            challenger: challengerCount,
            watching: watchingCount
          });

          return updatedNotifications;
        });
      }
    },
    []
  );

  return {
    notifications,
    loading,
    pagination,
    unreadCounts,
    actions: {
      fetchNotifications,
      markAsRead,
      markAsSeen,
      markAsSeenById,
      sendInvite,
      updateUnreadCounts,
      addRealTimeNotification,
      updateNotificationStatus,
    },
  };
};
